import QuillResizeImage from "quill-resize-image";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Placeholder,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import { useLocation } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import Search from "../../components/DstIcons/Search";
import DstTabs from "../../components/DstTabs/DstTabs";
import Lister from "../../components/Lister/Lister";
import { ListItem } from "../../components/Lister/Lister.d";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import { Pole } from "../../models/Pole";
import { Program } from "../../models/Program";
import { TemplateMessage } from "../../models/TemplateMessage";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { AddTemplateForm, EditTemplateForm, SelectedTemplate } from "./Messages.component";
import { ImageObject } from "./Messages.d";
import {
  RECIPIENT_PAGING_SIZE,
  deleteTemplate,
  fetchPoles,
  fetchPrograms,
  generateMessagePreview,
  handleAddTemplateSubmit,
  handleCancel,
  handleTemplateSelect,
  listItemMessages,
  loadMessagesAndTemplates,
  messagesRecipientTypes,
  resumeReceivers,
  selectForEditing,
  sendMessage,
  startAddingNewTemplate,
  submitEdit,
  summarizeReceivers,
} from "./Messages.function";
import "./Messages.scss";
import { CheatsheetOffCanvas, restrictedPlaceholders } from "./components/MessagesCheatsheet";

Quill.register("modules/resize", QuillResizeImage);

const Messages = () => {
  const { state } = useLocation() as any;
  const preselectedCohort =
    state?.cohortId && state?.cohortName
      ? [
          {
            category: "cohorts",
            value: {
              id: state.cohortId,
              name: state.cohortName,
            },
          },
        ]
      : [];

  const [images, setImages] = useState<Array<ImageObject>>([]);
  const { language, user_id, first_name, last_name } = useSession();
  const [messages, setMessages] = useState<Array<any>>([]);
  const [templates, setTemplates] = useState<TemplateMessage[]>([]);
  const [loaded, setLoaded] = useState<any>({
    messages: false,
    templates: false,
  });
  const [activeKey, setActiveKey] = useState("send-message");
  const [message, setMessage] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [authorFilter, setAuthorFilter] = useState(-1);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [selectedItemsAlias, setSelectedItemsAlias] = useState<any[]>([]);
  const [isMultiSelectLiveDropdownDisabled, setIsMultiSelectLiveDropdownDisabled] = useState(false);
  const [isMultiSelectDropdownDisabled, setIsMultiSelectDropdownDisabled] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateMessage | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState("");
  const [editingTemplateContent, setEditingTemplateContent] = useState("");
  const [showEditForm, setShowEditForm] = useState(false);
  const [isAddingNewTemplate, setIsAddingNewTemplate] = useState(false);
  const [recipientsVisibleCount, setRecipientsVisibleCount] = useState(RECIPIENT_PAGING_SIZE);
  const reactQuillRef = useRef<ReactQuill>(null);
  const [previewMessage, setPreviewMessage] = useState<string | { key: string; content: string }[]>([]);
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState<string | null>(null);
  const [poles, setPoles] = useState<Pole[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedPole, setSelectedPole] = useState<number | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [languageFilter, setLanguageFilter] = useState<string[]>([]);
  const [poleFilter, setPoleFilter] = useState<number[]>([]);
  const [programFilter, setProgramFilter] = useState<number[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [isMessageRetroactive, setIsMessageRetroactive] = useState(false);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("multiple", "multiple");
    input.click();
    input.onchange = () => {
      const { files } = input;
      if (input !== null && files !== null) {
        const filesArray = Array.from(files);
        const quill = reactQuillRef.current?.getEditor();
        filesArray.forEach((image) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const range = quill?.getSelection(true);
            if (range) {
              const imgSrc = e.target?.result;
              quill?.insertEmbed(range.index, "image", imgSrc);
              const imgElement = quill?.root.querySelector(`img[src="${imgSrc}"]`);
              setImages((prevImages) => [...prevImages, { file: image, element: `${imgElement?.outerHTML}` }]);
              quill?.setSelection(range.index + 1, 0);
            }
          };
          reader.readAsDataURL(image);
        });
      }
    };
  }, [reactQuillRef]);

  const quillToolbarModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: "1" }, { header: "2" }, { font: [] }],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
          ["link", "image"],
          ["code-block"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
      resize: {
        locale: {},
      },
    }),
    [imageHandler]
  );
  const [showCheatsheetPopup, setShowCheatsheetPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadMessagesAndTemplates(setMessages, setTemplates, setLoaded);
  }, []);

  useEffect(() => {
    fetchPoles(setPoles);
    fetchPrograms(setPrograms);
  }, []);

  useEffect(() => {
    if (!selectedItems.some((item) => item.category === "cohorts")) {
      setIsMessageRetroactive(false);
    }
  }, [selectedItems]);

  const handleSelect = (k: any) => setActiveKey(k);

  const handleOpenLink = (key: string, value: string) => {
    const modifiedKey = key.endsWith("s") ? key.slice(0, -1) : key;

    if (key !== "aliases") {
      return () => window.open(`/${modifiedKey}/${value}`);
    }
    return undefined;
  };

  const handleSelectTemplate = (templateId: number) => {
    const template = templates.find((t) => t.id === templateId);

    if (template) {
      setMessage(template.content);
      setSelectedTemplateId(templateId);
    }
  };

  const isSendDisabled = useMemo(() => {
    const allItemsAreCohorts = selectedItems.every((item) => item.category === "cohorts");
    const containsRestrictedPlaceholder = restrictedPlaceholders.some((placeholder) => message.includes(placeholder));
    const noRecipientsSelected = selectedItems.length === 0 && selectedItemsAlias.length === 0;

    if (containsRestrictedPlaceholder && !allItemsAreCohorts) {
      setErrorMessage(translate(language, "PAGES.MESSAGES.RESTRICTED_PLACEHOLDER_ERROR"));
      return true;
    }

    setErrorMessage("");
    return noRecipientsSelected;
  }, [language, message, selectedItems, selectedItemsAlias]);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState<{
    author: any;
    content: string;
    receivers: any[];
    parameters: Record<string, string | string[]>;
    identified_cohorts: any[];
  }>({
    author: null,
    content: "",
    receivers: [],
    parameters: {},
    identified_cohorts: [],
  });

  const handlePreview = () => {
    const cohortIds = selectedItems.filter((item) => item.category === "cohorts").map((item) => item.value.id);
    const userIds = selectedItems.filter((item) => item.category === "users").map((item) => item.value.id);
    const aliases = selectedItemsAlias.map((item) => item.name);

    generateMessagePreview(message, cohortIds, userIds, aliases, setPreviewMessage, () => {
      setShowPreviewPopup(true);
    });
  };

  const openMessagePopup = (messageId: number) => {
    const message = messages.find((message) => message.id === messageId);

    if (message) {
      const { author, content, receivers, parameters, identified_cohorts } = message;
      setPopupMessage({
        author,
        content,
        receivers,
        parameters: JSON.parse(
          parameters.replace(/('(?=(,\s*')))|('(?=:))|((?<=([:,[]\s*))')|((?<={)')|('(?=]|}))/g, '"')
        ),
        identified_cohorts: identified_cohorts || [],
      });
      setShowPopup(true);
    }
  };

  const columns = [
    { header: translate(language, "PAGES.MESSAGES.AUTHOR_HEADER"), key: "author_name" },
    { header: translate(language, "PAGES.MESSAGES.RECIPIENTS_HEADER"), key: "name" },
    { header: translate(language, "PAGES.MESSAGES.CONTENT_HEADER"), key: "content" },
    {
      header: translate(language, "PAGES.MESSAGES.CREATION_DATE_HEADER"),
      key: "date",
      render: (item: any) => (
        <div>
          <OverlayTrigger overlay={<Tooltip>{formatDateHumanly(language, item["date"], true)}</Tooltip>}>
            <span>{formatDateHumanly(language, item["date"])}</span>
          </OverlayTrigger>
        </div>
      ),
    },
    {
      header: translate(language, "PAGES.MESSAGES.RETROACTIVE_MESSAGE_HEADER"),
      key: "is_retroactive",
      render: (item: any) =>
        item.is_retroactive ? (
          <span className="dst-icon-check text-success" />
        ) : (
          <span className="dst-icon-x-close text-danger" />
        ),
    },
  ];

  const filteredItems: ListItem[] = useMemo(() => {
    const filteredByAuthor = listItemMessages(
      language,
      messages.filter((message) => [-1, message.author.id].includes(authorFilter))
    );

    return filteredByAuthor.filter((item) =>
      Object.values(item).join(" ").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [language, messages, authorFilter, searchQuery]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(
      (template) =>
        (!languageFilter.length || (template.language !== null && languageFilter.includes(template.language))) &&
        (!poleFilter.length || (template.pole !== null && poleFilter.includes(template.pole))) &&
        (!programFilter.length || (template.program !== null && programFilter.includes(template.program))) &&
        template.name &&
        template.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [templates, searchQuery, languageFilter, poleFilter, programFilter]);

  const languageTags = useMemo(
    () =>
      Array.from(new Set(templates.map((template) => template.language).filter(Boolean))).map((lang) => ({
        value: lang!,
        display: lang!,
      })),
    [templates]
  );

  const tabsData = [
    {
      id: "send-message",
      title: (
        <React.Fragment>
          <span className="dst-icon-send-03 me-2" />
          {translate(language, "PAGES.MESSAGES.SEND_MESSAGE_TAB_TITLE")}
        </React.Fragment>
      ),
      content: (
        <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/messages" }]} showFallBack>
          <div className="m-regular border rounded p-4">
            <Row className="mb-2">
              <Col className="m-regular">
                <InputGroup className="mb-3 custom-input-group multi-select-dropdown">
                  <InputGroup.Text>
                    <span className="dst-icon-users-03" />
                  </InputGroup.Text>
                  <MultiSelectLiveDropdown
                    category={["users", "cohorts"]}
                    label={`${translate(language, "PAGES.MESSAGES.TO_LABEL")} ${resumeReceivers(selectedItems)}`}
                    language={language}
                    mock={!(loaded.messages && loaded.templates)}
                    mockClass="rounded-0 rounded-end"
                    onConfirm={(selectedRecipients: any[]) => {
                      setSelectedItems(selectedRecipients);
                      setIsMultiSelectDropdownDisabled(selectedRecipients.length > 0);
                    }}
                    showFilterButton={true}
                    toggleClass="min-w-100px m-regular"
                    disabled={isMultiSelectLiveDropdownDisabled}
                    clearItemsOnSend={isSending}
                    initialSelectedItems={preselectedCohort}
                  />
                  <MultiSelectDropdown
                    label={`${translate(language, "PAGES.MESSAGES.TO_LABEL")} ${summarizeReceivers(
                      selectedItemsAlias
                    )}`}
                    displayItem={(item: any) => (
                      <div>{translate(language, `PAGES.MESSAGES.RECIPIENT_TYPES.${item.name.toUpperCase()}`)}</div>
                    )}
                    badgeItemProperty="name"
                    items={messagesRecipientTypes}
                    onConfirm={(selectedRecipients: any[]) => {
                      setSelectedItemsAlias(selectedRecipients);
                      setIsMultiSelectLiveDropdownDisabled(selectedRecipients.length > 0);
                    }}
                    language={language}
                    mock={!(loaded.messages && loaded.templates)}
                    showAlwaysConfirm={true}
                    disabled={isMultiSelectDropdownDisabled}
                    clearItemsOnSend={isSending}
                  />
                </InputGroup>
              </Col>
              <Col className="text-end d-flex justify-content-end">
                <Form.Group
                  controlId="templateSelect"
                  className="border d-flex rounded custom-input-group"
                  style={{ minWidth: "fit-content" }}
                >
                  <InputGroup className="border-0 rounded">
                    <InputGroup.Text className="border-0">
                      <span className="dst-icon-file-06" />
                    </InputGroup.Text>
                    {loaded["templates"] ? (
                      <MultiSelectDropdown
                        label={
                          (selectedTemplateId &&
                            templates.find((template) => template.id === selectedTemplateId)?.name) ||
                          translate(language, "PAGES.MESSAGES.CHOOSE_TEMPLATE")
                        }
                        displayItem={(item: any) => <div>{item.name}</div>}
                        badgeItemProperty="name"
                        items={templates}
                        onConfirm={(selectedRecipients: any[]) => {
                          handleSelectTemplate(selectedRecipients[0].id);
                        }}
                        isSingleSelect
                        language={language}
                        mock={!(loaded.messages && loaded.templates)}
                        showAlwaysConfirm
                        clearItemsOnSend={isSending}
                      />
                    ) : (
                      <Placeholder animation="glow">
                        <Placeholder
                          as="select"
                          className="form-control border-0 border-start m-regular"
                          style={{
                            width: "20ch",
                            paddingLeft: "0.5em",
                            height: "100%",
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                          }}
                        />
                      </Placeholder>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <ReactQuill
              value={message || ""}
              onChange={(content) => setMessage(content)}
              placeholder={translate(language, "PAGES.MESSAGES.WRITE_MESSAGE_PLACEHOLDER")}
              className={`mb-3 ${errorMessage ? "error-border" : ""}`}
              preserveWhitespace
              ref={reactQuillRef}
              modules={quillToolbarModules}
            />
            {errorMessage && <div className="text-danger mb-3">{errorMessage}</div>}
            <Row>
              <Col className="m-regular text-start">
                <DstButton
                  btnClass="m-bold"
                  variant="secondary"
                  clickFunction={() => setShowCheatsheetPopup((show) => !show)}
                  value={translate(language, "PAGES.MESSAGES.CHEATSHEET.BUTTON_TITLE")}
                  btnImageAfter={<span className="dst-icon-help-hexagon ms-1" />}
                />
                <DstButton
                  btnClass="m-bold mx-2"
                  variant="secondary"
                  clickFunction={handlePreview}
                  value={translate(language, "PAGES.MESSAGES.PREVIEW.BUTTON_TITLE")}
                  btnImageAfter={<span className="dst-icon-eye ms-1" />}
                  disabled={!message.trim().length || message.trim() === "<p><br></p>"}
                />
              </Col>
              <Col className="m-regular text-end">
                <DstButton
                  btnClass="m-bold"
                  variant="primary"
                  disabled={isSending || isSendDisabled}
                  clickFunction={() => {
                    setIsSending(true);
                    sendMessage(
                      selectedItems,
                      selectedItemsAlias,
                      images,
                      message,
                      language,
                      setActiveKey,
                      setMessage,
                      setMessages,
                      setTemplates,
                      setLoaded,
                      setSelectedItems,
                      setSelectedItemsAlias,
                      isMessageRetroactive,
                      () => {
                        setIsSending(false);
                        setIsMessageRetroactive(false);
                      }
                    );
                  }}
                  btnImageAfter={
                    isSending ? <Spinner className="ms-4" size="sm" /> : <span className="dst-icon-send-03 ms-4" />
                  }
                  value={translate(language, "PAGES.MESSAGES.SEND_MESSAGE")}
                />
                {(preselectedCohort.length || selectedItems.some((item) => item.category === "cohorts")) && (
                  <div className="d-flex justify-content-end align-items-center mt-2">
                    <Form.Check
                      type="checkbox"
                      id="is-retroactive-message"
                      label={
                        <label htmlFor="is-retroactive-message">
                          {translate(language, "PAGES.MESSAGES.MESSAGE_RETROACTIVE")}
                        </label>
                      }
                      checked={isMessageRetroactive}
                      onChange={(event) => {
                        setIsMessageRetroactive(event.target.checked);
                      }}
                    />
                    <OverlayTrigger
                      key="overlay-trigger-retroactive-message-infos"
                      placement="bottom"
                      overlay={
                        <Tooltip aria-label="Retroactive Message">
                          {translate(language, "PAGES.MESSAGES.MESSAGE_RETROACTIVE_EXPLAIN")}
                        </Tooltip>
                      }
                    >
                      <span className="clickable dst-icon-info-circle ms-2" />
                    </OverlayTrigger>
                  </div>
                )}
              </Col>
              {showPreviewPopup && (
                <Popup
                  show={showPreviewPopup}
                  onClose={() => setShowPreviewPopup(false)}
                  modalTitle={translate(language, "PAGES.MESSAGES.PREVIEW.TITLE")}
                  size="lg"
                  scrollable
                >
                  <div>
                    {Array.isArray(previewMessage) ? (
                      <DstTabs
                        tabsData={previewMessage.map(({ key, content }) => ({
                          id: key,
                          title: key,
                          content: (
                            <div className="medium-gray-background p-3 rounded my-3 my-md-4">
                              <div
                                id="message-preview-popup"
                                className="text-pre-wrap"
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                            </div>
                          ),
                        }))}
                        variant="primary"
                        activeKey={activePreviewTab || previewMessage[0].key}
                        onSelect={(key) => setActivePreviewTab(key)}
                      />
                    ) : (
                      <div className="medium-gray-background p-3 rounded my-3 my-md-4">
                        <div id="message-preview-popup" dangerouslySetInnerHTML={{ __html: previewMessage }} />
                      </div>
                    )}
                  </div>
                </Popup>
              )}
              <CheatsheetOffCanvas
                show={showCheatsheetPopup}
                onHide={() => setShowCheatsheetPopup(false)}
                language={language}
              />
            </Row>
          </div>
        </PermissionComponent>
      ),
    },
    {
      id: "manage-message",
      title: (
        <React.Fragment>
          <span className="dst-icon-git-commit me-2" />
          {translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES_TAB_TITLE")}
        </React.Fragment>
      ),
      content: (
        <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/messages/templates" }]} showFallBack>
          <div>
            {filtersVisible && (
              <div className="d-flex gap-2 mb-3">
                <MultiFilterDropdown
                  tags={languageTags}
                  onSelectTags={(tags) => setLanguageFilter(tags.map(String))}
                  defaultValue={languageFilter}
                  language={language}
                  inputTitle={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.FILTER.LANGUAGE")}
                  mock={!loaded.templates}
                />
                <MultiFilterDropdown
                  tags={poles.map((pole) => ({ display: pole.name, value: pole.id }))}
                  onSelectTags={(tags) => setPoleFilter(tags.map(Number))}
                  defaultValue={poleFilter}
                  showSearchBar
                  language={language}
                  inputTitle={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.FILTER.POLE")}
                  mock={!loaded.templates}
                />
                <MultiFilterDropdown
                  tags={programs.map((program) => ({ display: program.name, value: program.id }))}
                  onSelectTags={(tags) => setProgramFilter(tags.map((tag) => Number(tag)))}
                  defaultValue={programFilter}
                  showSearchBar
                  language={language}
                  inputTitle={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.FILTER.PROGRAM")}
                  mock={!loaded.templates}
                />
              </div>
            )}
            <Row>
              <Col lg={3}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">
                    {languageFilter.length || poleFilter.length || programFilter.length
                      ? translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.COUNT_TEMPLATE").replace(
                          "{{COUNT}}",
                          filteredTemplates.length.toString()
                        )
                      : translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.ALL_TEMPLATE")}
                  </h3>
                  <span
                    className={`dst-icon-filter-funnel-01 clickable ${filtersVisible ? "blue-font" : ""}`}
                    onClick={() => setFiltersVisible((prev) => !prev)}
                    title={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.APPLY_FILTER")}
                  />
                </div>
                <ListerSearch setSearchQuery={setSearchQuery} mock={!loaded} />
                <div className="overflow-auto max-h-template">
                  {!loaded.templates ? (
                    <React.Fragment>
                      {[...Array(4)].map((_, index) => (
                        <div key={index} className={`template-item clickable m-regular`}>
                          <div className="py-2">
                            <div className="px-2">
                              <Placeholder xs={10} />
                            </div>
                            <div className="px-2 xs-regular smoke-gray-font">
                              <Placeholder xs={6} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ) : (
                    filteredTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`template-item clickable m-regular ${
                          selectedTemplate && selectedTemplate.id === template.id ? "template-item-active" : ""
                        }`}
                        onClick={() =>
                          handleTemplateSelect(
                            template,
                            setSelectedTemplate,
                            setShowEditForm,
                            setEditingTemplateId,
                            setEditingTemplateName,
                            setEditingTemplateContent,
                            setIsAddingNewTemplate,
                            setSelectedPole,
                            setSelectedProgram,
                            setSelectedLanguage
                          )
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleTemplateSelect(
                              template,
                              setSelectedTemplate,
                              setShowEditForm,
                              setEditingTemplateId,
                              setEditingTemplateName,
                              setEditingTemplateContent,
                              setIsAddingNewTemplate,
                              setSelectedPole,
                              setSelectedProgram,
                              setSelectedLanguage
                            );
                          }
                        }}
                        tabIndex={0}
                      >
                        <div className="py-2">
                          <div className="px-2">{template.name}</div>
                          <div className="px-2 xs-regular smoke-gray-font">
                            {formatDateHumanly(language, template.dt_last_updated || template.dt_created)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-2 mt-lg-3 mb-3 mb-lg-0">
                  <DstButton
                    btnClass="d-flex align-items-center"
                    value={translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.USER_ACTION.ADD")}
                    variant="outline-primary"
                    btnImageAfter={<span className="dst-icon-plus ms-2"></span>}
                    clickFunction={() =>
                      startAddingNewTemplate(
                        setIsAddingNewTemplate,
                        setEditingTemplateId,
                        setTemplateName,
                        setTemplateContent,
                        setShowEditForm,
                        setSelectedTemplate,
                        setSelectedPole,
                        setSelectedProgram,
                        setSelectedLanguage,
                        language
                      )
                    }
                  />
                </div>
              </Col>
              <Col>
                {isAddingNewTemplate && (
                  <AddTemplateForm
                    language={language}
                    templateName={templateName}
                    templateContent={templateContent}
                    poles={poles}
                    programs={programs}
                    selectedPole={selectedPole ?? 0}
                    setSelectedPole={setSelectedPole}
                    selectedProgram={selectedProgram ?? 0}
                    setSelectedProgram={setSelectedProgram}
                    setSelectedLanguage={setSelectedLanguage}
                    selectedLanguage={selectedLanguage ?? language}
                    setTemplateName={setTemplateName}
                    setTemplateContent={setTemplateContent}
                    handleAddTemplateSubmit={() =>
                      handleAddTemplateSubmit(
                        templateName,
                        templateContent,
                        selectedPole,
                        setSelectedPole,
                        selectedProgram,
                        setSelectedProgram,
                        selectedLanguage,
                        setSelectedLanguage,
                        setTemplateName,
                        setTemplateContent,
                        setIsAddingNewTemplate,
                        setSelectedTemplate,
                        setShowEditForm,
                        loadMessagesAndTemplates,
                        setMessages,
                        setTemplates,
                        setLoaded,
                        language
                      )
                    }
                    handleCancel={() =>
                      handleCancel(
                        language,
                        setTemplateName,
                        setTemplateContent,
                        setShowEditForm,
                        setIsAddingNewTemplate,
                        setEditingTemplateId,
                        setSelectedPole,
                        setSelectedProgram,
                        setSelectedLanguage
                      )
                    }
                  />
                )}
                {showEditForm && editingTemplateId && (
                  <EditTemplateForm
                    editingTemplateName={editingTemplateName}
                    language={language}
                    poles={poles}
                    programs={programs}
                    selectedPole={selectedPole ?? 0}
                    setSelectedPole={setSelectedPole}
                    selectedProgram={selectedProgram ?? 0}
                    setSelectedProgram={setSelectedProgram}
                    setSelectedLanguage={setSelectedLanguage}
                    selectedTemplate={selectedTemplate}
                    setEditingTemplateName={setEditingTemplateName}
                    editingTemplateContent={editingTemplateContent}
                    setEditingTemplateContent={setEditingTemplateContent}
                    submitEdit={() =>
                      submitEdit(
                        selectedTemplate,
                        {
                          full_name: `${first_name} ${last_name}`,
                          id: user_id,
                        },
                        editingTemplateId,
                        editingTemplateName,
                        editingTemplateContent,
                        setSelectedTemplate,
                        setShowEditForm,
                        loadMessagesAndTemplates,
                        setMessages,
                        setTemplates,
                        setLoaded,
                        selectedPole,
                        setSelectedPole,
                        selectedProgram,
                        setSelectedProgram,
                        selectedLanguage,
                        setSelectedLanguage,
                        language
                      )
                    }
                    handleCancel={() =>
                      handleCancel(
                        language,
                        setTemplateName,
                        setTemplateContent,
                        setShowEditForm,
                        setIsAddingNewTemplate,
                        setEditingTemplateId,
                        setSelectedPole,
                        setSelectedProgram,
                        setSelectedLanguage
                      )
                    }
                    showForm={showEditForm || (isAddingNewTemplate && editingTemplateId)}
                  />
                )}
                {selectedTemplate && !showEditForm && (
                  <SelectedTemplate
                    selectedTemplate={selectedTemplate}
                    language={language}
                    selectForEditing={(template) =>
                      selectForEditing(
                        template,
                        setEditingTemplateId,
                        setEditingTemplateName,
                        setEditingTemplateContent,
                        setShowEditForm,
                        setIsAddingNewTemplate,
                        setSelectedPole,
                        setSelectedProgram,
                        setSelectedLanguage
                      )
                    }
                    deleteTemplate={() =>
                      selectedTemplate &&
                      deleteTemplate(
                        selectedTemplate.id,
                        setSelectedTemplate,
                        setMessages,
                        setTemplates,
                        setLoaded,
                        language
                      )
                    }
                  />
                )}
                {!isAddingNewTemplate && !selectedTemplate && !showEditForm && (
                  <div>{translate(language, "PAGES.MESSAGES.MANAGE_TEMPLATES.NOT_SELECTED")}</div>
                )}
              </Col>
            </Row>
          </div>
        </PermissionComponent>
      ),
    },
    {
      id: "message-sent",
      title: (
        <React.Fragment>
          <span className="dst-icon-git-commit me-2" />
          {translate(language, "PAGES.MESSAGES.MESSAGES_SENT_TAB_TITLE")}
        </React.Fragment>
      ),
      content: (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex">
              <h2 className="blue-font me-2 mb-0">{translate(language, "PAGES.MESSAGES.SENT_MESSAGES_TITLE")}</h2>
              <FormControl
                as="select"
                value={authorFilter || ""}
                onChange={(e) => setAuthorFilter(+e.target.value)}
                className="w-fit"
              >
                <option value={-1}>{translate(language, "PAGES.MESSAGES.ALL_OPTION")}</option>
                <option value={user_id!}>{translate(language, "PAGES.MESSAGES.MINE_OPTION")}</option>
              </FormControl>
            </div>
            <ListerSearch setSearchQuery={setSearchQuery} mock={!loaded.messages} />
          </div>
          <React.Fragment>
            <Lister
              items={filteredItems}
              columns={columns}
              mock={!loaded.messages}
              showHeader
              additionalButton={(item: any) => (
                <span className="clickable xs-regular" onClick={() => openMessagePopup(item.id)}>
                  <Search classes="search-icon m-regular" />
                </span>
              )}
              pagination={true}
            />
          </React.Fragment>
        </div>
      ),
    },
  ];

  return (
    <div className="p-3 p-md-4" id="messages-page">
      <div className="d-flex">
        <div className="me-3 align-self-center">
          <img src="./assets/images/message.png" alt={translate(language, "PAGES.MESSAGES.ENVELOPE_ALT")} height="80" />
        </div>
        <div>
          <h2>{translate(language, "PAGES.MESSAGES.SEND_MESSAGE_TITLE")}</h2>
          <div className="s-regular">
            {" "}
            {translate(language, "PAGES.MESSAGES.COMMUNICATE_EASILY")} <br />
            {translate(language, "PAGES.MESSAGES.PERSONALIZE_MESSAGE")}
          </div>
        </div>
      </div>
      <DstTabs tabsData={tabsData} variant="primary" classes="mt-3" activeKey={activeKey} onSelect={handleSelect} />
      {showPopup && (
        <Popup
          show={showPopup}
          onClose={() => {
            setShowPopup(false);
            setRecipientsVisibleCount(20);
          }}
          modalTitle={translate(language, "PAGES.MESSAGES.MODALE.TITLE")}
          size="xl"
          fullscreen="lg-down"
          scrollable={true}
        >
          <div>
            <h4>{translate(language, "PAGES.MESSAGES.MODALE.AUTHOR")}</h4>
            <DstButton
              btnClass="me-2 mb-3"
              variant="secondary"
              clickFunction={() => window.open(`/user/${popupMessage.author.id}`, "_blank")}
              value={popupMessage.author.email}
              btnImageAfter={<span className="dst-icon-link-external-02 ms-1" />}
            />
            {popupMessage.receivers && (
              <React.Fragment>
                <h4>{`${translate(language, "PAGES.MESSAGES.MODALE.RECIPIENTS")} (${
                  popupMessage.receivers.length
                })`}</h4>
                <div>
                  {popupMessage.identified_cohorts?.map((cohort: any, index: number) => (
                    <DstButton
                      btnClass="me-2"
                      variant="secondary"
                      key={`cohort-${index}`}
                      clickFunction={() => window.open(`/cohort/${cohort.id}`, "_blank")}
                      value={cohort.name}
                      btnImageAfter={<span className="dst-icon-link-external-02 ms-1" />}
                    />
                  ))}
                  {popupMessage.identified_cohorts.length > 0 && popupMessage.receivers.length > 0 && ", "}
                  {popupMessage.receivers.slice(0, recipientsVisibleCount).map((receiver: any, index: number) => (
                    <DstButton
                      key={`receiver-${index}`}
                      btnClass="me-2 my-2"
                      variant="secondary"
                      value={receiver.email}
                      btnImageAfter={<span className="dst-icon-link-external-02 ms-1" />}
                      clickFunction={() => window.open(`/user/${receiver.id}`, "_blank")}
                    />
                  ))}
                  {popupMessage.receivers.length > recipientsVisibleCount && (
                    <Button
                      variant="link"
                      onClick={() => setRecipientsVisibleCount((prevCount) => prevCount + 20)}
                      className="p-0"
                    >
                      {translate(language, "PAGES.MESSAGES.MODALE.MORE_BUTTON")}
                    </Button>
                  )}
                </div>
              </React.Fragment>
            )}
            {popupMessage.parameters && Object.keys(popupMessage.parameters).length > 0 && (
              <React.Fragment>
                <h4 className="mt-3">{translate(language, "PAGES.MESSAGES.MODALE.IN_DETAIL")}</h4>
                <div>
                  {Object.keys(popupMessage.parameters)
                    .filter((key) => ["users", "cohorts", "aliases", "meetings"].includes(key))
                    .map((key) => (
                      <div key={key} className="d-flex">
                        <p className="me-2 d-flex align-items-center">{`${key.charAt(0).toUpperCase()}${key.slice(
                          1
                        )}:`}</p>
                        {(popupMessage.parameters[key] as string).split(",").map((value: string, index: number) => {
                          return (
                            <DstButton
                              btnClass="me-2 mb-3"
                              variant="secondary"
                              clickFunction={handleOpenLink(key, value)}
                              key={`${key}-${value}-${index}`}
                              value={value}
                              btnImageAfter={
                                key !== "aliases" ? <span className="dst-icon-link-external-02 ms-1" /> : undefined
                              }
                            />
                          );
                        })}
                      </div>
                    ))}
                </div>
              </React.Fragment>
            )}
            <h4>{translate(language, "PAGES.MESSAGES.MODALE.CONTENT")}</h4>
            <div className="p-3 medium-gray-background border-rounded">
              <div
                id="message-popup-content"
                className="text-pre-wrap"
                dangerouslySetInnerHTML={{ __html: popupMessage.content }}
              />
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default Messages;
